import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';

/**
 * Admin Settings - System-wide configuration and settings
 */
export const Route = createFileRoute('/_authenticated/admin/settings')({
  component: AdminSettings,
});

function AdminSettings() {
  const [activeTab, setActiveTab] = useState<'general' | 'payments' | 'security' | 'integrations'>('general');
  const { theme } = useTheme();

  const colors = {
    dark: {
      text: '#ffffff',
      textMuted: '#9ca3af',
      tabBorder: 'rgba(220, 38, 38, 0.3)',
      tabActiveBg: 'rgba(220, 38, 38, 0.2)',
      tabActiveText: '#ef4444',
      tabInactiveText: '#d1d5db',
      tabActiveBorder: '#dc2626',
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(220, 38, 38, 0.3)',
      headerColor: '#ef4444'
    },
    light: {
      text: '#111827',
      textMuted: '#6b7280',
      tabBorder: '#e5e7eb',
      tabActiveBg: '#fef2f2',
      tabActiveText: '#991b1b',
      tabInactiveText: '#7f1d1d',
      tabActiveBorder: '#dc2626',
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      headerColor: '#dc2626'
    }
  };

  const c = colors[theme];

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            color: c.headerColor,
            marginBottom: '0.5rem'
          }}>
            System Settings
          </h1>
          <p style={{ color: c.textMuted, fontSize: '1rem' }}>
            Configure platform-wide settings and integrations
          </p>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: `2px solid ${c.tabBorder}`,
          marginBottom: '2rem',
          gap: '0.5rem'
        }}>
          {['general', 'payments', 'security', 'integrations'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: activeTab === tab ? c.tabActiveBg : 'transparent',
                color: activeTab === tab ? c.tabActiveText : c.tabInactiveText,
                border: 'none',
                borderBottom: activeTab === tab ? `2px solid ${c.tabActiveBorder}` : '2px solid transparent',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                textTransform: 'capitalize',
                marginBottom: '-2px'
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{
          background: c.cardBg,
          border: `1px solid ${c.cardBorder}`,
          borderRadius: '0.5rem',
          padding: '2rem',
          backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none'
        }}>
          {activeTab === 'general' && <GeneralSettings theme={theme} />}
          {activeTab === 'payments' && <PaymentSettings theme={theme} />}
          {activeTab === 'security' && <SecuritySettings theme={theme} />}
          {activeTab === 'integrations' && <IntegrationSettings theme={theme} />}
        </div>
      </div>
    </div>
  );
}

interface TabContentProps {
  theme: 'dark' | 'light';
}

function GeneralSettings({ theme }: TabContentProps) {
  const colors = {
    dark: {
      text: '#ffffff',
      textMuted: '#9ca3af',
      headerColor: '#ef4444',
      buttonBg: '#dc2626'
    },
    light: {
      text: '#111827',
      textMuted: '#6b7280',
      headerColor: '#dc2626',
      buttonBg: '#dc2626'
    }
  };

  const c = colors[theme];

  return (
    <div>
      <h2 style={{
        fontSize: '1.25rem',
        fontWeight: '600',
        color: c.headerColor,
        marginBottom: '1.5rem'
      }}>
        General Settings
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <SettingField
          label="Platform Name"
          value="patchGrid"
          description="The name displayed across the platform"
          theme={theme}
        />
        <SettingField
          label="Support Email"
          value="support@patchgrid.dev"
          description="Email address for customer support inquiries"
          theme={theme}
        />
        <SettingField
          label="Commission Rate"
          value="15%"
          description="Platform commission on each sale"
          theme={theme}
        />
        <div style={{ marginTop: '1rem' }}>
          <button style={{
            backgroundColor: c.buttonBg,
            color: 'white',
            padding: '0.75rem 1.5rem',
            borderRadius: '0.375rem',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}>
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

function PaymentSettings({ theme }: TabContentProps) {
  const colors = {
    dark: {
      text: '#ffffff',
      textMuted: '#9ca3af',
      headerColor: '#ef4444',
      buttonBg: '#dc2626',
      infoBg: 'rgba(220, 38, 38, 0.1)',
      infoBorder: 'rgba(220, 38, 38, 0.3)'
    },
    light: {
      text: '#111827',
      textMuted: '#6b7280',
      headerColor: '#dc2626',
      buttonBg: '#dc2626',
      infoBg: '#fef2f2',
      infoBorder: '#fecaca'
    }
  };

  const c = colors[theme];

  return (
    <div>
      <h2 style={{
        fontSize: '1.25rem',
        fontWeight: '600',
        color: c.headerColor,
        marginBottom: '1.5rem'
      }}>
        Payment Settings
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <SettingField
          label="Stripe Publishable Key"
          value="pk_test_..."
          description="Your Stripe publishable key"
          type="password"
          theme={theme}
        />
        <SettingField
          label="Stripe Secret Key"
          value="sk_test_..."
          description="Your Stripe secret key (stored in AWS Secrets Manager)"
          type="password"
          theme={theme}
        />
        <div style={{
          padding: '1rem',
          backgroundColor: c.infoBg,
          borderRadius: '0.375rem',
          border: `1px solid ${c.infoBorder}`
        }}>
          <p style={{ fontSize: '0.875rem', color: c.textMuted, margin: 0 }}>
            💡 Payment keys are managed through AWS Secrets Manager for security
          </p>
        </div>
        <div style={{ marginTop: '1rem' }}>
          <button style={{
            backgroundColor: c.buttonBg,
            color: 'white',
            padding: '0.75rem 1.5rem',
            borderRadius: '0.375rem',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}>
            Update Payment Settings
          </button>
        </div>
      </div>
    </div>
  );
}

function SecuritySettings({ theme }: TabContentProps) {
  const colors = {
    dark: {
      text: '#ffffff',
      headerColor: '#ef4444',
      buttonBg: '#dc2626'
    },
    light: {
      text: '#111827',
      headerColor: '#dc2626',
      buttonBg: '#dc2626'
    }
  };

  const c = colors[theme];

  return (
    <div>
      <h2 style={{
        fontSize: '1.25rem',
        fontWeight: '600',
        color: c.headerColor,
        marginBottom: '1.5rem'
      }}>
        Security Settings
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <div>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            cursor: 'pointer'
          }}>
            <input type="checkbox" defaultChecked />
            <span style={{ fontSize: '0.875rem', color: c.text, fontWeight: '500' }}>
              Require email verification for new users
            </span>
          </label>
        </div>
        <div>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            cursor: 'pointer'
          }}>
            <input type="checkbox" defaultChecked />
            <span style={{ fontSize: '0.875rem', color: c.text, fontWeight: '500' }}>
              Enable two-factor authentication
            </span>
          </label>
        </div>
        <div>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            cursor: 'pointer'
          }}>
            <input type="checkbox" />
            <span style={{ fontSize: '0.875rem', color: c.text, fontWeight: '500' }}>
              Require manual review for new creator accounts
            </span>
          </label>
        </div>
        <div style={{ marginTop: '1rem' }}>
          <button style={{
            backgroundColor: c.buttonBg,
            color: 'white',
            padding: '0.75rem 1.5rem',
            borderRadius: '0.375rem',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}>
            Save Security Settings
          </button>
        </div>
      </div>
    </div>
  );
}

function IntegrationSettings({ theme }: TabContentProps) {
  const colors = {
    dark: {
      headerColor: '#ef4444'
    },
    light: {
      headerColor: '#dc2626'
    }
  };

  const c = colors[theme];

  return (
    <div>
      <h2 style={{
        fontSize: '1.25rem',
        fontWeight: '600',
        color: c.headerColor,
        marginBottom: '1.5rem'
      }}>
        Integration Settings
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <IntegrationCard
          name="WorkOS AuthKit"
          status="configured"
          description="Authentication and user management"
          theme={theme}
        />
        <IntegrationCard
          name="AWS Cognito"
          status="configured"
          description="Identity pool and access management"
          theme={theme}
        />
        <IntegrationCard
          name="Stripe"
          status="configured"
          description="Payment processing"
          theme={theme}
        />
        <IntegrationCard
          name="CloudFront CDN"
          status="configured"
          description="Asset delivery and caching"
          theme={theme}
        />
      </div>
    </div>
  );
}

interface SettingFieldProps {
  label: string;
  value: string;
  description: string;
  type?: string;
  theme: 'dark' | 'light';
}

function SettingField({ label, value, description, type = 'text', theme }: SettingFieldProps) {
  const colors = {
    dark: {
      text: '#ffffff',
      textMuted: '#9ca3af',
      inputBg: 'rgba(26, 26, 46, 0.6)',
      inputBorder: 'rgba(220, 38, 38, 0.3)',
      descriptionColor: '#9ca3af'
    },
    light: {
      text: '#111827',
      textMuted: '#6b7280',
      inputBg: '#ffffff',
      inputBorder: '#e5e7eb',
      descriptionColor: '#9ca3af'
    }
  };

  const c = colors[theme];

  return (
    <div>
      <label style={{
        display: 'block',
        fontSize: '0.875rem',
        fontWeight: '600',
        color: c.text,
        marginBottom: '0.5rem'
      }}>
        {label}
      </label>
      <input
        type={type}
        defaultValue={value}
        style={{
          width: '100%',
          padding: '0.75rem',
          border: `1px solid ${c.inputBorder}`,
          borderRadius: '0.375rem',
          fontSize: '0.875rem',
          marginBottom: '0.25rem',
          backgroundColor: c.inputBg,
          color: c.text
        }}
      />
      <p style={{
        fontSize: '0.75rem',
        color: c.descriptionColor,
        margin: 0
      }}>
        {description}
      </p>
    </div>
  );
}

interface IntegrationCardProps {
  name: string;
  status: 'configured' | 'not-configured';
  description: string;
  theme: 'dark' | 'light';
}

function IntegrationCard({ name, status, description, theme }: IntegrationCardProps) {
  const colors = {
    dark: {
      cardBg: 'rgba(220, 38, 38, 0.1)',
      cardBorder: 'rgba(220, 38, 38, 0.3)',
      text: '#ffffff',
      textMuted: '#9ca3af',
      buttonBg: 'rgba(26, 26, 46, 0.6)',
      buttonBorder: 'rgba(220, 38, 38, 0.3)',
      buttonText: '#d1d5db'
    },
    light: {
      cardBg: '#fef2f2',
      cardBorder: '#e5e7eb',
      text: '#111827',
      textMuted: '#6b7280',
      buttonBg: 'white',
      buttonBorder: '#e5e7eb',
      buttonText: '#991b1b'
    }
  };

  const c = colors[theme];

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem',
      backgroundColor: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      borderRadius: '0.375rem'
    }}>
      <div>
        <h3 style={{
          fontSize: '1rem',
          fontWeight: '600',
          color: c.text,
          marginBottom: '0.25rem'
        }}>
          {name}
        </h3>
        <p style={{
          fontSize: '0.75rem',
          color: c.textMuted,
          margin: 0
        }}>
          {description}
        </p>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <span style={{
          padding: '0.25rem 0.75rem',
          borderRadius: '9999px',
          fontSize: '0.75rem',
          fontWeight: '600',
          backgroundColor: status === 'configured' ? '#d1fae5' : '#fee2e2',
          color: status === 'configured' ? '#065f46' : '#991b1b'
        }}>
          {status === 'configured' ? 'Configured' : 'Not Configured'}
        </span>
        <button style={{
          padding: '0.5rem 1rem',
          backgroundColor: c.buttonBg,
          border: `1px solid ${c.buttonBorder}`,
          borderRadius: '0.375rem',
          cursor: 'pointer',
          fontSize: '0.75rem',
          color: c.buttonText,
          fontWeight: '500'
        }}>
          Configure
        </button>
      </div>
    </div>
  );
}

export default AdminSettings;
